import { JiraAssignedTicket } from '@Models/types';
import {
  Faders,
  MagnifyingGlass,
  SquaresFour,
  X,
  ArrowClockwise,
} from '@phosphor-icons/react';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { MoonLoader } from 'react-spinners';
import Ticket from './Ticket';
import { useTicketContext } from '@Context/TicketContext';
import Filter from './Filter';

const TicketList = () => {
  const { tickets, fetchTickets, isLoading, error } = useTicketContext();

  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [filteredSearch, setFilteredSearch] =
    useState<JiraAssignedTicket[]>(tickets);

  // Fetch everything on mount
  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleRefresh = async () => {
    try {
      await fetchTickets({ force: true });
    } catch (error) {
      console.error('Manual refresh failed', error);
    }
  };

  const applySearchFilter = useCallback(
    (items: JiraAssignedTicket[], q: string) => {
      const query = q.trim().toLowerCase();
      if (!query) return items;
      return items.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.record_type.toLowerCase().includes(query)
      );
    },
    []
  );

  useEffect(() => {
    setFilteredSearch(applySearchFilter(tickets, search));
  }, [tickets, search, applySearchFilter]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearch(query);
    setFilteredSearch(
      tickets.filter(
        (item) =>
          item.name.toLowerCase().includes(query.toLowerCase()) ||
          item.record_type.toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  const clearSearch = () => {
    setSearch('');
    setFilteredSearch(tickets);
  };

  const handleFilter = (filter: string) => {
    setFilter(filter);
    if (filter === 'All Tickets') {
      setFilteredSearch(tickets);
    } else {
      setFilteredSearch(
        tickets.filter((item) => {
          const status = getTicketStatus(item);
          return status.includes(filter.toLowerCase());
        })
      );
    }
  };

  const toggleDropdown = () => setIsOpen(!isOpen);

  const getTicketStatus = (ticket: JiraAssignedTicket): string => {
    return (
      ticket.workflow_status?.name?.toLowerCase() ||
      ticket.status?.toLowerCase() ||
      ''
    );
  };

  const { openTickets, doneTickets } = useMemo(() => {
    const open = filteredSearch.filter(
      (ticket) => getTicketStatus(ticket) !== 'done'
    );
    const done = filteredSearch.filter(
      (ticket) => getTicketStatus(ticket) === 'done'
    );
    return { openTickets: open, doneTickets: done };
  }, [filteredSearch]);

  return (
    <div className="flex h-full w-full flex-col rounded-lg bg-white">
      <span className="relative flex w-full flex-col items-center justify-between rounded-t-md bg-primary-700 px-4 py-2 min-[450px]:flex-row">
        <div className="flex w-full flex-row items-center justify-between">
          <span className="font-extrabold text-white">Tickets</span>
        </div>

        <div className="mt-2 flex w-full flex-row items-center min-[620px]:justify-end sm:mt-0">
          <div className="mr-2 flex w-full flex-row items-center gap-x-2 rounded-md border bg-white px-2 py-1 sm:w-56">
            <MagnifyingGlass size={20} weight="bold" className="shrink-0" />
            <input
              type="text"
              className="w-full min-w-0 border-gray-200 font-inter text-sm focus:outline-none"
              placeholder="Search"
              value={search}
              onChange={handleSearch}
            />
            <X
              data-testid="test-ticket-list-clear-search-btn"
              weight="bold"
              className="shrink-0 cursor-pointer hover:text-brandRed-500"
              onClick={clearSearch}
            />
          </div>

          <Faders
            data-testid="test-ticket-list-filter-btn"
            onClick={toggleDropdown}
            className="cursor-pointer text-white hover:text-primary-200"
            size={23}
            weight="bold"
          />
        </div>

        {isOpen && (
          <Filter
            selectedFilter={filter}
            handleFilter={handleFilter}
            toggleDropdown={toggleDropdown}
          />
        )}

        <button
          onClick={handleRefresh}
          disabled={isLoading}
          title="Refresh Tickets"
          className="ml-2 inline-flex items-center justify-center rounded-md py-0.5 text-xs font-medium text-white hover:text-primary-200"
        >
          <ArrowClockwise size={16} weight="bold" />
        </button>
      </span>

      <div className="relative mx-2 mb-2 flex h-0 flex-grow flex-col">
        {/* Error state */}
        {error && (
          <div className="mx-2 my-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {!isLoading && tickets.length === 0 ? (
          <span className="flex h-full flex-col items-center justify-center text-neutral-300">
            <SquaresFour size={150} weight="bold" />
            <label className="text-sm text-neutral-500">
              No assigned Jira tickets
            </label>
          </span>
        ) : null}

        {isLoading && tickets.length > 0 && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-md bg-white/70 bg-opacity-90 backdrop-blur-[1px]">
            <div className="flex flex-col items-center gap-3">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-700 border-t-transparent"></div>
              <span className="text-xs font-medium text-gray-700">
                Refreshing...
              </span>
            </div>
          </div>
        )}

        {isLoading && tickets.length === 0 ? (
          <span className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-5">
            <MoonLoader size={100} color="#071D49" />
            <span className="animate-pulse text-sm font-semibold tracking-wide text-gray-600">
              Loading Tickets...
            </span>
          </span>
        ) : (
          <div className="custom-scroll flex h-0 flex-grow flex-col gap-y-1 overflow-y-auto p-2">
            {/* Open Tickets Section */}
            {openTickets.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs font-bold text-gray-600">
                  Open Tickets
                </div>
                {openTickets.map((ticket) => (
                  <Ticket key={`open-${ticket.id}`} ticket={ticket} />
                ))}
              </>
            )}

            {/* Done Tickets Section */}
            {doneTickets.length > 0 && (
              <>
                <div className="px-2 py-1 text-xs font-bold text-gray-600">
                  Done Tickets
                </div>
                {doneTickets.map((ticket) => (
                  <Ticket key={`done-${ticket.id}`} ticket={ticket} />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketList;
